// The complete-title bundle, resolved once for whichever surface is offering
// it — the paywall plate (`premium-gate`) and the library shelf
// (`library-page`), which asked the same four questions in the same order and
// had already drifted apart on the answers.
//
// RESOLVED FROM CONFIG, NEVER INVENTED: a title has a bundle only when its
// `BookConfig` names the id it already sold under (`legacyBundleProductId`).
// Every single-volume book, and any future multi-volume title with no bundle
// minted yet, gets no CTA rather than a made-up one.
//
// Entitlement is deliberately NOT here. The shelf must ask (it renders for a
// reader who may already own the bundle); the plate must not (it only renders
// once the gate found the reader unentitled to this chapter, and a bundle owner
// is entitled to every volume through the series fan-out, so they can never see
// it) — and adding the query there would open a live subscription per locked
// chapter view for an answer that is known.

import { useQuery } from "convex/react";
import { api } from "@bindery/convex/_generated/api";
import { useLastLoaded } from "@/lib/use-last-loaded";
import {
  BOOK_CONFIGS,
  type BookConfig,
} from "@bindery/convex/model/bookConfig";
import {
  paywalledVolumeCount,
  publishedPaywalledVolumeCount,
  volumeAvailabilityNote,
} from "@bindery/convex/model/volume";
import type { SaleOffer } from "./sale-price";
import type { CatalogBook } from "@bindery/shared/corpus/catalog";

/** The live bundle offer — `checkout.getProduct`'s shape, which is also
 *  `SaleOffer` plus the name, so either surface can render it its own way. */
export interface BundleProduct extends SaleOffer {
  displayName: string;
}

interface BundleOffer {
  /** The book's config, or `undefined` for a catalog book config has forgotten. */
  config: BookConfig | undefined;
  /** The bundle's product id, or `undefined` when this title has no bundle. */
  bundleProductId: string | undefined;
  /** The live offer; `undefined` while it loads or when there is nothing to
   *  load, `null` when nothing prices the id. Both render as "no price yet",
   *  which is why every call site tests `!= null`. */
  product: BundleProduct | null | undefined;
  /** "3 of 7 volumes ready now — the rest as they publish.", or `null` when the
   *  complete run is already readable. */
  availabilityNote: string | null;
}

/**
 * `now` is the CALLER's coarse clock (`useCoarseNow`), not one of ours: both
 * surfaces already hold one for their own price queries, and a second clock
 * here would tick on its own schedule and re-subscribe twice as often.
 */
export function useBundleOffer(book: CatalogBook, now: number): BundleOffer {
  const config = BOOK_CONFIGS[book.slug];
  const bundleProductId = config?.legacyBundleProductId;
  // Held across the minute tick, so the price doesn't unmount for a round trip
  // once a minute and shift the layout under the reader (`useLastLoaded`).
  const product = useLastLoaded(
    useQuery(
      api.checkout.getProduct,
      bundleProductId ? { productId: bundleProductId, now } : "skip",
    ),
  );
  // What a shopper can actually open today, from the catalog THIS surface
  // already fetched — the reader-facing half of the pair documented on
  // `volumeIsPublished`.
  const availabilityNote =
    config !== undefined && bundleProductId !== undefined
      ? volumeAvailabilityNote(
          publishedPaywalledVolumeCount(
            config,
            new Set(book.chapters.map((c) => c.number)),
          ),
          paywalledVolumeCount(config),
        )
      : null;
  return { config, bundleProductId, product, availabilityNote };
}
